// CSS
import './ContactNew.css';
// React hooks
import { useEffect, useState } from 'react';
// Skybase hooks
import { API_BASE_URL } from '../../config';
// Skybase components
import NavBarNew from '../../components/NavBarNew/NavBarNew';
import FooterNew from '../../components/FooterNew/FooterNew';
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover';
// External hooks/components
import { useMediaQuery } from "react-responsive";               // responsive CSS
// Media assets
import AnimatedHorizontalLogo from '../../assets/videos/skybase-framer-logo.webm';


// ==========
// ContactNew – represents the /contact Landing Page.
// ==========
function ContactNew() {
    
    // state vars
    const [topic, setTopic] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [details, setDetails] = useState('');

    // responsive CSS
    const isDesktop = useMediaQuery({ minWidth: 1025 });
    const isTablet = useMediaQuery({ minWidth: 701 });
    const isPhone = useMediaQuery({ maxWidth: 700 });


    // ======================
    // Form onSubmit Behavior
    // ======================
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    const handleContactFormSubmission = (e) => {
        
        // Prevent default HTML form behavior (page refresh)
        e.preventDefault();

        // `POST /features/contact/`
        submitContactFormMessage();

        // Reset all form vals
        setTopic('feedback');
        setContactEmail('');
        setFirstName('');
        setLastName('');
        setDetails('');

        // Display InformationPopover
        setIsInformationalPopoverOpen(true);
    }


    // ==========================
    // submitContactFormMessage()
    // ==========================
    // Runs `POST /contact/` with a ContactFormMessagePayload.
    async function submitContactFormMessage(){

        // Create the JSON Payload (In Axum: features/contact/model.rs::ContactFormMessagePayload)
        const json_payload = {
            topic: topic,
            email: contactEmail,
            first_name: firstName,
            last_name: lastName,
            message: details,
        }

        // `POST /contact/`
        const url = new URL(`${API_BASE_URL}/api/v1/contact`);
        const res = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(json_payload),
        });

        // Handle any error or log success message into console
        if (!res.ok) {
            throw new Error(`Skybase (Client) – HTTP StatusCode ${res.status} Error: Unable to POST Contact Form submission.`);
        } else {
            console.log("Skybase (Client) – Successful Contact Form submission.");
        }
    }


    // Adjust ContactFormVerticalTitleText
    const [verticalFormTitleText, setVerticalFormTitleText] = useState("Contact our customer support");
    useEffect(() => {
        if (isTablet) {
            setVerticalFormTitleText("Contact our customer support");
        } else {
            setVerticalFormTitleText("Contact us");
        }
    }, [isTablet]);


    // JSX Component
    return(
        <div className='ContactNewPage'>
            
            {/* Aesthetic background */}
            <div className='ContactNewPage-aesthetic-bg'></div>

            {/* NavBarNew */}
            <NavBarNew/>

            {/* Main content */}
            <div className='ContactNewPage-main-content'>

                {/* Contact form (normal) */}
                { isDesktop && 
                    <form className='ContactNewPage-contact-form SearchPage-fade-in' onSubmit={(e) => handleContactFormSubmission(e)}>

                        {/* Left content */}
                        <div className='ContactNewPage-contact-form--left-half'>
                            
                            {/* Form title */}
                            <div className='ContactNewPage-contact-form-title'>
                                Contact our<br />
                                customer support
                            </div>

                            {/* Form subtitle */}
                            <div className='ContactNewPage-contact-form-subtitle'>
                                You can also email us at <span className='ContactNewPage-contact-form-subtitle--hyperlink' onClick={() => window.location.href = "mailto:contact@skybasedata.com"}>contact@skybasedata.com</span>
                            </div>

                            {/* Logo */}
                            <video
                                className='ContactNewPage-animated-logo'
                                autoPlay
                                loop
                                muted
                                playsInline>
                                <source src={AnimatedHorizontalLogo} type="video/webm"/>
                            </video>
                        </div>

                        {/* Right content */}
                        <div className='ContactNewPage-contact-form--right-half'>
                            
                            {/* Row */}
                            <div className='ContactNewPage-contact-form-row'>
                                
                                {/* Input: Topic */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>Topic *</div>
                                    <select
                                        className='ContactNewPage-form-input--dropdown'
                                            id="category"
                                            value={topic}
                                            onChange={(e) => setTopic(e.target.value)}
                                        >
                                        <option value={"sales-and-pricing"}>Sales/Pricing</option>
                                        <option value={"product-questions"}>Product Questions</option>
                                        <option value={"technical-support"}>Technical Support</option>
                                        <option value={"billing-and-accounts"}>Billing/Account</option>
                                        <option value={"security-and-privacy"}>Security/Privacy</option>
                                        <option value={"feature-request"}>Feature Request</option>
                                        <option value={"bug-report"}>Bug Report</option>
                                        <option value={"general-feedback"}>General Feedback</option>
                                        <option value={"other/unspecified"}>Other</option>
                                            
                                    </select>
                                    
                                </div>

                                {/* Input: Contact Email */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>Contact email *</div>
                                    <input
                                    className='ContactNewPage-form-input--field'
                                    type="contactEmail"
                                    value={contactEmail}
                                    placeholder="yourname@example.com"
                                    onChange={(e) => setContactEmail(e.target.value)}
                                    required/>
                                </div>
                            </div>

                            {/* Row */}
                            <div className='ContactNewPage-contact-form-row'>
                                
                                {/* Input: First Name */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>First name *</div>
                                    <input
                                    className='ContactNewPage-form-input--field'
                                    type="firstName"
                                    value={firstName}
                                    placeholder="First name"
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required/>
                                </div>

                                {/* Input: Last Name */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>Last name *</div>
                                    <input
                                    className='ContactNewPage-form-input--field'
                                    type="lastName"
                                    value={lastName}
                                    placeholder="Last name"
                                    onChange={(e) => setLastName(e.target.value)}
                                    required/>
                                </div>
                            </div>

                            {/* Row */}
                            <div className='ContactNewPage-contact-form-row'>
                                
                                {/* Input: Details */}
                                <div className='ContactNewPage-form-input--full'>
                                    <div className='ContactNewPage-form-input--title'>Details *</div>
                                    <textarea
                                    className="ContactNewPage-form-input--text-area"
                                    value={details}
                                    placeholder="Max 500 characters"
                                    onChange={(e) => setDetails(e.target.value)}
                                    maxLength={500}
                                    required
                                    rows={5}
                                    />
                                </div>
                            </div>

                            {/* Row */}
                            <div className='ContactNewPage-form-input--full'>
                                
                                {/* Submit button */}
                                <button
                                    type='submit'
                                    className='ContactNewPage-contact-form-submit-button'>
                                    Submit
                                </button>
                            </div>
                        </div>
                    </form>
                }

                {/* Contact form (mobile) */}
                { !isDesktop &&
                    <form className='ContactNewPage-contact-form-vertical SearchPage-fade-in' onSubmit={(e) => handleContactFormSubmission(e)}>

                        {/* Header */}
                        <div className='ContactNewPage-contact-form-vertical-header'>{verticalFormTitleText}</div>

                        {/* Fields */}
                        <div className='ContactNewPage-contact-form-vertical-fields'>

                            {/* Row */}
                            <div className='ContactNewPage-contact-form-row'>
                                
                                {/* Input: Topic */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>Topic *</div>
                                    <select
                                        className='ContactNewPage-form-input--dropdown'
                                            id="category"
                                            value={topic}
                                            onChange={(e) => setTopic(e.target.value)}
                                        >
                                        <option value={"sales-and-pricing"}>Sales/Pricing</option>
                                        <option value={"product-questions"}>Product Questions</option>
                                        <option value={"technical-support"}>Technical Support</option>
                                        <option value={"billing-and-accounts"}>Billing/Account</option>
                                        <option value={"security-and-privacy"}>Security/Privacy</option>
                                        <option value={"feature-request"}>Feature Request</option>
                                        <option value={"bug-report"}>Bug Report</option>
                                        <option value={"general-feedback"}>General Feedback</option>
                                        <option value={"other/unspecified"}>Other</option>
                                            
                                    </select>
                                    
                                </div>

                                {/* Input: Contact Email */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>Contact email *</div>
                                    <input
                                    className='ContactNewPage-form-input--field'
                                    type="contactEmail"
                                    value={contactEmail}
                                    placeholder="yourname@example.com"
                                    onChange={(e) => setContactEmail(e.target.value)}
                                    required/>
                                </div>
                            </div>

                            {/* Row */}
                            <div className='ContactNewPage-contact-form-row'>
                                
                                {/* Input: First Name */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>First name *</div>
                                    <input
                                    className='ContactNewPage-form-input--field'
                                    type="firstName"
                                    value={firstName}
                                    placeholder="First name"
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required/>
                                </div>

                                {/* Input: Last Name */}
                                <div className='ContactNewPage-form-input--half'>
                                    <div className='ContactNewPage-form-input--title'>Last name *</div>
                                    <input
                                    className='ContactNewPage-form-input--field'
                                    type="lastName"
                                    value={lastName}
                                    placeholder="Last name"
                                    onChange={(e) => setLastName(e.target.value)}
                                    required/>
                                </div>
                            </div>

                            {/* Row */}
                            <div className='ContactNewPage-contact-form-row'>
                                {/* Input: Details */}
                                <div className='ContactNewPage-form-input--full'>
                                    <div className='ContactNewPage-form-input--title'>Details *</div>
                                    <textarea
                                    className="ContactNewPage-form-input--text-area"
                                    value={details}
                                    placeholder="Max 500 characters"
                                    onChange={(e) => setDetails(e.target.value)}
                                    maxLength={500}
                                    required
                                    rows={5}
                                    />
                                </div>
                            </div>

                            {/* Row */}
                            <div className='ContactNewPage-form-input--full'>   
                                {/* Submit button */}
                                <button
                                    type='submit'
                                    className='ContactNewPage-contact-form-submit-button'>
                                    Submit
                                </button>
                            </div>

                        </div>

                    </form>
                }

            </div>

            {/* Footer new */}
            <FooterNew/>

            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Form Submitted Successfully"}
                    bodyText={"Thank you for reaching out. Our customer service team will reply to your submission within a few hours."}
                    primaryButtonText={"Close"}
                    primaryButtonCallback={() => setIsInformationalPopoverOpen(false)}
                />
            }

        </div>
    )
};



// Export statement
export default ContactNew;